import {useMemo} from 'react';

import {
    addToSelection,
    clearSelection as clearSelectionAction,
    removeFromSelection,
} from '../reducers';
import {selectionSelector} from '../selectors';
import {
    useAppDispatch,
    useAppSelector,
} from '../../../hooks/useTypedReactRedux';

import {
    DnsRecord,
    recordKey,
} from '../types';

// FNP-1192 — selection.
//
// FNP-1293: this used to be a module-scoped `let globalSelection` plus a `Set`
// of listener callbacks — state living outside React. That shape survived
// unmount and route changes, so an operator who ticked a record, navigated to
// another zone and came back found the checkbox still ticked. It also outlived
// a test case, which is why it shipped with a `resetDnsRecordsSelection` export
// for the suite to call by hand: a workaround for the leak, not a fix.
//
// Selection now lives in a Redux slice, as the module checklist §3 requires and
// as domain-names, registrars and top-level-domains already do in this app.
// Test isolation is structural — the render harness builds a fresh store per
// test — so the hand-rolled reset is gone.
//
// ⚠️ KEYED BY `recordKey`, NOT BY AN ID, because a DNS record HAS NO ID. Its
// identity is (name, type) within its zone. That is why this module's slice is
// hand-rolled rather than built from `SelectionSliceFor`, which keys on
// `record.id` — see reducers/index.ts for the full reasoning. The same
// `recordKey` feeds `MantineDataTable`'s `idAccessor`, so the key the table
// marks a row selected by and the key the store files it under cannot diverge.
//
// ⚠️ THE FULL RECORD IS THE STORED VALUE, not just the key. A delete needs the
// name, type, TTL and values to build the change.
//
// ⚠️ THE HOOK'S CONTRACT IS UNCHANGED on purpose: `selectedRecords` is still an
// ARRAY and `setSelectedRecords` still takes one, because that is what
// `MantineDataTable`'s `selectedRecords` / `onSelectedRecordsChange` pair
// speaks. This hook is the only place that converts between array and map, so
// no caller had to change.
export const useDnsRecordsSelection = () => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));

    // Memoised on the slice: a fresh array identity every render would make the
    // table read an unchanged selection as a change.
    const selectedRecords = useMemo(
        () => Object.values(selection) as DnsRecord[],
        [selection],
    );

    // ⚠️ The table hands back the WHOLE next selection rather than a delta, so
    // this diffs against what is stored and dispatches only the rows that
    // actually entered or left.
    const setSelectedRecords = (next: DnsRecord[]) => {

        const nextKeys = new Set(next.map((record) => recordKey(record)));

        Object.keys(selection)
              .filter((key) => !nextKeys.has(key))
              .forEach((key) => dispatch(removeFromSelection(key)));

        next.filter((record) => typeof selection[recordKey(record)] === 'undefined')
            .forEach((record) => dispatch(addToSelection(record)));
    };

    return {
        selectedRecords,
        setSelectedRecords,
        clearSelection: () => dispatch(clearSelectionAction()),
    };
};
